<template>
    <Card>
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_RECONCILIATION">
            <div class="my-3">
                <router-link to="/reconciliations">
                    <Button type="button" :noMargin="true"
                        ><i class="fas fa-chevron-left" aria-hidden="true"></i> Back to reconciliations</Button
                    >
                </router-link>
            </div>

            <UiState v-if="loading" type="loading" title="Loading reconciliation details" compact />
            <UiState
                v-else-if="loadError"
                type="error"
                :message="loadError"
                actionLabel="Try again"
                compact
                @action="fetchReconciliationDetails"
            />
            <UiState
                v-else-if="!reconciliation"
                type="empty"
                title="Reconciliation not found"
                compact
            />

            <div v-else>
                <div class="p-3 my-4 reconciliation-metadata" style="border: 1px solid #ddd">
                    <div class="row gy-3">
                        <div class="col-md-6">Name: {{ reconciliation.name }}</div>
                        <div class="col-md-6">Created By: {{ reconciliation.modified_by }}</div>
                        <div class="col-md-6">Warehouse: {{ warehouse ? warehouse.name : "" }}</div>
                        <div class="col-md-6">
                            Room Type: {{ reconciliation.is_back_room ? "Back Room" : "Front Room" }}
                        </div>
                        <div class="col-md-6">
                            Created Date: {{ usDateFormat(reconciliation.createdAt) }}
                        </div>
                        <div class="col-md-6">Total Items: {{ reconciliationItems.length }}</div>
                        <div class="col-md-6">
                            Status:
                            <span :class="['review-status', `review-status--${statusKey}`]">{{ statusLabel }}</span>
                        </div>
                        <div class="col-md-6">Submitted By: {{ reconciliation.submitted_by || reconciliation.modified_by || "-" }}</div>
                        <div v-if="reconciliation.reviewed_at" class="col-md-6">
                            Reviewed: {{ usDateFormat(reconciliation.reviewed_at) }}
                        </div>
                        <div v-if="reconciliation.reviewed_by" class="col-md-6">
                            Reviewed By: {{ reconciliation.reviewed_by }}
                        </div>
                    </div>
                </div>

                <p class="reconciliation-note">
                    {{ statusDescription }}
                </p>

                <div v-if="reconciliation.review_note" class="review-note">
                    <strong>Review note</strong>
                    <p>{{ reconciliation.review_note }}</p>
                </div>

                <AllowPermission v-if="isPending" :permission="PERMISSION_CODES.CMS_EDIT_RECONCILIATION">
                    <section class="review-panel" aria-labelledby="review-heading">
                        <div>
                            <h2 id="review-heading">Upper-management review</h2>
                            <p>
                                Approval applies every package delta once. If live inventory changed after submission,
                                approval stops and requires a fresh count.
                            </p>
                        </div>
                        <label for="reconciliation-review-note">Review note</label>
                        <textarea
                            id="reconciliation-review-note"
                            v-model.trim="reviewNote"
                            class="form-control"
                            rows="3"
                            maxlength="2000"
                            placeholder="Optional for approval; required when rejecting"
                        ></textarea>
                        <p v-if="hasLegacySelections" class="review-warning">
                            This older count does not identify exact packages and cannot be approved. Recount it in the updated POS.
                        </p>
                        <div class="review-actions">
                            <Button
                                type="button"
                                variant="danger"
                                :noMargin="true"
                                :disabled="reviewBusy"
                                @click="reviewReconciliation('reject')"
                            >Reject</Button>
                            <Button
                                type="button"
                                :noMargin="true"
                                :disabled="reviewBusy || hasLegacySelections"
                                @click="reviewReconciliation('approve')"
                            >{{ reviewBusy ? "Please wait..." : "Approve and apply" }}</Button>
                        </div>
                    </section>
                </AllowPermission>

                <dl class="reconciliation-summary" aria-label="Reconciliation difference summary">
                    <div>
                        <dt>Total</dt>
                        <dd>{{ deltaSummary.total }}</dd>
                    </div>
                    <div>
                        <dt>Mismatches</dt>
                        <dd>{{ deltaSummary.mismatch }}</dd>
                    </div>
                    <div>
                        <dt>Over</dt>
                        <dd>{{ deltaSummary.over }}</dd>
                    </div>
                    <div>
                        <dt>Under</dt>
                        <dd>{{ deltaSummary.under }}</dd>
                    </div>
                    <div>
                        <dt>Matched</dt>
                        <dd>{{ deltaSummary.matched }}</dd>
                    </div>
                </dl>

                <div class="reconciliation-filters" role="group" aria-label="Filter reconciliation items">
                    <button
                        v-for="option in filterOptions"
                        :key="option.value"
                        type="button"
                        :class="['reconciliation-filter', { 'reconciliation-filter--active': currentFilter === option.value }]"
                        :aria-pressed="String(currentFilter === option.value)"
                        @click="currentFilter = option.value"
                    >
                        {{ option.label }} <span aria-hidden="true">({{ option.count }})</span>
                    </button>
                </div>

                <UiState
                    v-if="!reconciliationItems.length"
                    type="empty"
                    title="No reconciliation items"
                    message="This reconciliation does not contain any submitted item counts."
                    compact
                />
                <UiState
                    v-else-if="!filteredReconciliationItems.length"
                    type="empty"
                    :title="`No ${filterLabel} items`"
                    message="Choose another filter to review the submitted counts."
                    compact
                />

                <CustomTable v-else>
                    <template #head>
                        <tr>
                            <th>Item Name</th>
                            <th>Image</th>
                            <th>Package / Lot</th>
                            <th>Selection</th>
                            <th>Stock Qty Snapshot</th>
                            <th>Stock Qty Reconciled</th>
                            <th>Delta</th>
                        </tr>
                    </template>
                    <template #body>
                        <tr v-for="item of filteredReconciliationItems" :key="item.id">
                            <td>{{ item.name }}</td>
                            <td>
                                <img
                                    v-if="item.image"
                                    :src="item.image"
                                    :alt="`${item.name || 'Reconciliation item'} thumbnail`"
                                    class="reconciliation-thumbnail"
                                />
                                <span v-else class="reconciliation-image-placeholder">No image</span>
                            </td>
                            <td>{{ item.batch_no || "Not recorded" }}</td>
                            <td>{{ selectionLabel(item.selection_mode) }}</td>
                            <td>{{ expectedStockLabel(item) }}</td>
                            <td>{{ item.stock_qty_reconciled }}</td>
                            <td>
                                <span
                                    :class="['reconciliation-delta', deltaClass(item.delta)]"
                                    :aria-label="deltaAriaLabel(item.delta)"
                                >
                                    {{ formatDelta(item.delta) }}
                                </span>
                            </td>
                        </tr>
                    </template>
                </CustomTable>

                <section v-if="auditEvents.length" class="audit-trail" aria-labelledby="audit-heading">
                    <h2 id="audit-heading">Audit trail</h2>
                    <ol>
                        <li v-for="event in auditEvents" :key="event.id">
                            <strong>{{ eventLabel(event.event) }}</strong>
                            <span>{{ event.actor_name || "System" }} · {{ usDateFormat(event.createdAt) }}</span>
                            <p v-if="event.note">{{ event.note }}</p>
                        </li>
                    </ol>
                </section>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapActions } from "vuex"
import CustomTable from "../../components/layout/CustomTable.vue"
import { http } from "../../utils/http"
import Card from "../../components/UI/Card.vue"
import { confirmDialog, usDateFormat } from "../../utils/common"
import Button from "../../components/UI/Button.vue"
import UiState from "../../components/UI/UiState.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    data() {
        return {
            PERMISSION_CODES,
            warehouse: null,
            reconciliation: null,
            reconciliationItems: [],
            auditEvents: [],
            currentFilter: "all",
            reviewNote: "",
            reviewBusy: false,
            loading: true,
            loadError: "",
        }
    },
    components: { AllowPermission, Button, Card, CustomTable, UiState },
    mounted() {
        this.setData({
            pageTitle: "Reconciliation Details",
        })
        this.fetchReconciliationDetails()
    },

    computed: {
        normalizedStatus() {
            return this.reconciliation && this.reconciliation.status
                ? this.reconciliation.status
                : "PENDING_REVIEW"
        },
        isPending() {
            return this.normalizedStatus === "PENDING_REVIEW"
        },
        statusKey() {
            return this.normalizedStatus.toLowerCase().replace("_", "-")
        },
        statusLabel() {
            return {
                PENDING_REVIEW: "Pending review",
                APPROVED: "Approved",
                REJECTED: "Rejected",
            }[this.normalizedStatus] || "Pending review"
        },
        statusDescription() {
            if (this.normalizedStatus === "APPROVED") {
                return "Approved package counts were applied to inventory exactly once. The original stock snapshot and applied deltas remain in the audit record."
            }
            if (this.normalizedStatus === "REJECTED") {
                return "This count was rejected without changing inventory. Review the reason and submit a fresh reconciliation if needed."
            }
            return "Each submitted package count is compared with the stock snapshot captured at submission. Inventory has not changed; approval requires live stock to still match that snapshot."
        },
        hasLegacySelections() {
            return this.reconciliationItems.some(
                (item) => !item.selection_mode || item.selection_mode === "LEGACY_ITEM" || !item.batch_no
            )
        },
        itemsWithDelta() {
            return this.reconciliationItems.map((item) => ({
                ...item,
                delta: this.quantity(item.stock_qty_reconciled) - this.quantity(item.stock_qty),
            }))
        },
        deltaSummary() {
            return this.itemsWithDelta.reduce(
                (summary, item) => {
                    summary.total += 1
                    if (item.delta > 0) {
                        summary.over += 1
                        summary.mismatch += 1
                    } else if (item.delta < 0) {
                        summary.under += 1
                        summary.mismatch += 1
                    } else {
                        summary.matched += 1
                    }
                    return summary
                },
                { total: 0, mismatch: 0, over: 0, under: 0, matched: 0 }
            )
        },
        filterOptions() {
            return [
                { value: "all", label: "All", count: this.deltaSummary.total },
                { value: "mismatch", label: "Mismatch", count: this.deltaSummary.mismatch },
                { value: "over", label: "Over", count: this.deltaSummary.over },
                { value: "under", label: "Under", count: this.deltaSummary.under },
                { value: "matched", label: "Matched", count: this.deltaSummary.matched },
            ]
        },
        filterLabel() {
            const option = this.filterOptions.find((entry) => entry.value === this.currentFilter)
            return option ? option.label.toLowerCase() : "matching"
        },
        filteredReconciliationItems() {
            const items = this.itemsWithDelta.filter((item) => {
                if (this.currentFilter === "mismatch") return item.delta !== 0
                if (this.currentFilter === "over") return item.delta > 0
                if (this.currentFilter === "under") return item.delta < 0
                if (this.currentFilter === "matched") return item.delta === 0
                return true
            })

            return items.slice().sort((first, second) => {
                const firstMatched = first.delta === 0
                const secondMatched = second.delta === 0
                if (firstMatched !== secondMatched) return firstMatched ? 1 : -1

                const differenceMagnitude = Math.abs(second.delta) - Math.abs(first.delta)
                if (differenceMagnitude) return differenceMagnitude

                const nameOrder = String(first.name || "").localeCompare(String(second.name || ""), undefined, {
                    sensitivity: "base",
                })
                if (nameOrder) return nameOrder
                return String(first.id || "").localeCompare(String(second.id || ""))
            })
        },
    },
    methods: {
        usDateFormat,
        ...mapActions("misc", ["setData"]),
        quantity(value) {
            const numericValue = Number(value)
            return Number.isFinite(numericValue) ? numericValue : 0
        },
        formatDelta(delta) {
            return delta > 0 ? `+${delta}` : String(delta)
        },
        deltaClass(delta) {
            if (delta > 0) return "reconciliation-delta--over"
            if (delta < 0) return "reconciliation-delta--under"
            return "reconciliation-delta--matched"
        },
        deltaAriaLabel(delta) {
            if (delta > 0) return `${delta} over the stock snapshot`
            if (delta < 0) return `${Math.abs(delta)} under the stock snapshot`
            return "Matches the stock snapshot"
        },
        selectionLabel(mode) {
            if (mode === "EXISTING_LOT") return "Existing lot"
            if (mode === "NEW_LOT") return "New lot"
            return "Legacy item count"
        },
        expectedStockLabel(item) {
            if (this.reconciliation?.blind_count && (item?.stock_qty === undefined || item?.stock_qty === null)) {
                return "Hidden for blind count"
            }
            return item?.stock_qty ?? "-"
        },
        eventLabel(event) {
            return {
                SUBMITTED: "Submitted for review",
                APPROVED: "Approved and applied",
                REJECTED: "Rejected",
            }[event] || event
        },
        async reviewReconciliation(action) {
            if (this.reviewBusy || !this.reconciliation) return
            if (action === "reject" && !this.reviewNote) {
                this.$toast.error("Enter a reason before rejecting this reconciliation.")
                return
            }
            const approved = await confirmDialog({
                title: action === "approve" ? "Approve and apply inventory?" : "Reject reconciliation?",
                text:
                    action === "approve"
                        ? "The submitted package counts will be applied if live stock still matches the captured snapshot."
                        : "Inventory will not change and the counter will need to submit a fresh reconciliation.",
                confirmButtonText: action === "approve" ? "Approve and apply" : "Reject",
            })
            if (!approved) return
            this.reviewBusy = true
            try {
                const reconciliationId = encodeURIComponent(this.$route.params.id)
                const response = await http.post(`/reconciliations/${reconciliationId}/${action}`, {
                    review_note: this.reviewNote || null,
                })
                this.$toast.success(response.data.message)
                await this.fetchReconciliationDetails()
            } catch (err) {
                const message = err && err.response && err.response.data && err.response.data.message
                this.$toast.error(message || `Could not ${action} reconciliation.`)
            } finally {
                this.reviewBusy = false
            }
        },
        async fetchReconciliationDetails() {
            this.loading = true
            this.loadError = ""
            try {
                const reconciliationId = encodeURIComponent(this.$route.params.id)
                const res = await http.get(`/reconciliations/${reconciliationId}`)
                this.reconciliation = res.data.result || null
                this.reconciliationItems = Array.isArray(res.data.reconciliationItems)
                    ? res.data.reconciliationItems
                    : []
                this.warehouse = res.data.warehouse || null
                this.auditEvents = Array.isArray(res.data.auditEvents) ? res.data.auditEvents : []
                this.reviewNote = ""
                this.currentFilter = "all"
            } catch (err) {
                console.log("error in fetching reconciliation items", err)
                this.reconciliation = null
                this.reconciliationItems = []
                this.warehouse = null
                this.auditEvents = []
                this.loadError = "Could not load reconciliation details."
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.reconciliation-metadata {
  border-color: $lightBorderColor !important;
  border-radius: $cardRadius;
  box-shadow: $cardShadow;
}

.reconciliation-note {
  max-width: 760px;
  margin: 0 0 $sectionGap;
  color: rgba($textColor, 0.74);
  font-size: 0.9rem;
}

.review-status {
  display: inline-flex;
  margin-left: 6px;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba($warningColor, 0.14);
  color: #7a4a00;
  font-size: 0.78rem;
  font-weight: 700;
}

.review-status--approved {
  background: rgba($successColor, 0.14);
  color: #287a4a;
}

.review-status--rejected {
  background: rgba($dangerColor, 0.12);
  color: #b42318;
}

.review-note,
.review-panel,
.audit-trail {
  margin: 0 0 $sectionGap;
  padding: 16px;
  border: 1px solid $lightBorderColor;
  border-radius: $cardRadius;
  background: $lightCardBg;
}

.review-note p,
.review-panel p,
.audit-trail p {
  margin: 6px 0 0;
}

.review-panel h2,
.audit-trail h2 {
  margin: 0;
  font-size: 1rem;
}

.review-panel label {
  display: block;
  margin: 14px 0 6px;
  font-weight: 600;
}

.review-warning {
  color: #b42318;
  font-weight: 600;
}

.review-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}

.audit-trail ol {
  display: grid;
  gap: 10px;
  margin: 12px 0 0;
  padding-left: 20px;
}

.audit-trail li span {
  display: block;
  color: rgba($textColor, 0.7);
  font-size: 0.82rem;
}

.reconciliation-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 10px;
  margin: 0 0 $sectionGap;
}

.reconciliation-summary > div {
  padding: 12px;
  border: 1px solid $lightBorderColor;
  border-radius: $cardRadius;
  background: $lightCardBg;
}

.reconciliation-summary dt {
  color: rgba($textColor, 0.7);
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
}

.reconciliation-summary dd {
  margin: 4px 0 0;
  color: $primaryColor;
  font-size: 1.25rem;
  font-weight: 700;
}

.reconciliation-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: $sectionGap;
}

.reconciliation-filter {
  padding: 7px 12px;
  border: 1px solid $lightBorderColor;
  border-radius: 999px;
  background: $lightCardBg;
  color: $textColor;
  font-size: 0.84rem;
  font-weight: 600;
}

.reconciliation-filter:hover,
.reconciliation-filter:focus-visible,
.reconciliation-filter--active {
  border-color: $primaryColor;
  color: $primaryColor;
}

.reconciliation-filter:focus-visible {
  outline: 2px solid $primaryColor;
  outline-offset: 2px;
}

.reconciliation-thumbnail {
  width: 50px;
  height: 50px;
  border-radius: 4px;
  object-fit: cover;
}

.reconciliation-image-placeholder {
  color: rgba($textColor, 0.62);
  font-size: 0.8rem;
}

.reconciliation-delta {
  display: inline-flex;
  min-width: 42px;
  justify-content: flex-end;
  font-weight: 700;
}

.reconciliation-delta--over {
  color: #9c4f00;
}

.reconciliation-delta--under {
  color: #b42318;
}

.reconciliation-delta--matched {
  color: #287a4a;
}

:deep(.dark) .reconciliation-metadata {
  border-color: $darkBorderColor !important;
}

:deep(.dark) .reconciliation-note,
:deep(.dark) .reconciliation-image-placeholder {
  color: rgba($darkTextColor, 0.76);
}

:deep(.dark) .review-status {
  color: $warningColorDark;
}

:deep(.dark) .review-status--approved {
  color: $successColorDark;
}

:deep(.dark) .review-status--rejected,
:deep(.dark) .review-warning {
  color: #ff8a80;
}

:deep(.dark) .review-note,
:deep(.dark) .review-panel,
:deep(.dark) .audit-trail {
  border-color: $darkBorderColor;
  background: $darkInputBg;
  color: $darkTextColor;
}

:deep(.dark) .audit-trail li span {
  color: rgba($darkTextColor, 0.72);
}

:deep(.dark) .reconciliation-summary > div {
  border-color: $darkBorderColor;
  background: $darkInputBg;
}

:deep(.dark) .reconciliation-summary dt {
  color: rgba($darkTextColor, 0.7);
}

:deep(.dark) .reconciliation-summary dd {
  color: $secondaryColorDark;
}

:deep(.dark) .reconciliation-filter {
  border-color: $darkBorderColor;
  background: $darkInputBg;
  color: $darkTextColor;
}

:deep(.dark) .reconciliation-filter:hover,
:deep(.dark) .reconciliation-filter:focus-visible,
:deep(.dark) .reconciliation-filter--active {
  border-color: $secondaryColorDark;
  color: $secondaryColorDark;
}

:deep(.dark) .reconciliation-filter:focus-visible {
  outline-color: $secondaryColorDark;
}

:deep(.dark) .custom-table th {
  background: $darkInputBg;
  color: $darkTextColor;
}

:deep(.dark) .custom-table td {
  background: $darkCardBg;
  color: $darkTextColor;
}

:deep(.dark) .reconciliation-delta--over {
  color: $warningColorDark;
}

:deep(.dark) .reconciliation-delta--under {
  color: #ff8a80;
}

:deep(.dark) .reconciliation-delta--matched {
  color: $successColorDark;
}

/* Align numeric columns */
::deep(table) {
  thead th:nth-child(5),
  thead th:nth-child(6),
  thead th:nth-child(7) {
    text-align: right;
  }
  tbody td:nth-child(5),
  tbody td:nth-child(6),
  tbody td:nth-child(7) {
    text-align: right;
  }
}

@media (max-width: 640px) {
  .review-panel,
  .review-note,
  .audit-trail {
    padding: 12px;
  }

  .review-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }
}
</style>
